import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import { useArchivePatientMutation } from "apollo/hooks/mutations";
import { useSenderAccountContext } from "context/SenderAccountContext";
import { TRACK_EVENTS } from "core/consts";
import { hasAccessToDoctorDashboard } from "core/model/accounts";
import { AUCTION_RUNNING_STATUSES } from "core/model/auctions";
import { Patient } from "core/types";
import InfoChip from "ds_legacy/components/InfoChip";
import RSButton from "ds_legacy/components/RSButton";
import { DISABLED_COLOR, PRIMARY_COLOR } from "ds_legacy/materials/colors";
import { FONT_SIZE_13, FONT_SIZE_17 } from "ds_legacy/materials/typography";
import { useToast } from "dsl/atoms/ToastNotificationContext";
import { useCareseekerNavigationHandlers, useModal } from "dsl/hooks";
import { ChevronRightIcon } from "lucide-react";
import { useTracking } from "react-tracking";
import { useTranslations } from "translations";

function ArchivePatientModal({
  isOpen,
  onClose,
  patient,
}: {
  isOpen: boolean;
  onClose: () => void;
  patient: Patient;
}) {
  const translations = useTranslations();
  const { trackEvent } = useTracking();
  const toast = useToast();
  const navigationHandlers = useCareseekerNavigationHandlers();
  const { activeRoles } = useSenderAccountContext();

  const [archivePatient, queryProgress] = useArchivePatientMutation({
    id: patient.id,
    onCompleted: () => {
      toast({
        color: "primary",
        message: translations.timeline.patientArchivedNotification,
      });
      trackEvent({ name: TRACK_EVENTS.ARCHIVE_PATIENT_PATIENT_HOME });
      onClose();

      if (hasAccessToDoctorDashboard(activeRoles)) {
        navigationHandlers.goToDoctorDashboard();
      } else {
        navigationHandlers.goToTodo();
      }
    },
    onError: () => {
      toast({
        color: "danger",
        message: translations.auctionRequest.tryAgain,
      });
    },
  });

  return (
    <Dialog fullWidth open={isOpen} onClose={onClose}>
      <DialogTitle>
        {translations.patient.deletePatient.confirmText({
          patientName: patient?.user_id || "",
        })}
      </DialogTitle>
      <DialogContent>
        <DialogContentText>
          {translations.search.archivePatientDescription}
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button className="cancel" color="primary" onClick={onClose}>
          {translations.actions.cancel}
        </Button>
        <RSButton
          className="confirm"
          color="primary"
          id="archive"
          inverted
          loading={queryProgress}
          variant="text"
          onClick={() => archivePatient()}
        >
          {translations.actions.archiveButton}
        </RSButton>
      </DialogActions>
    </Dialog>
  );
}

export function ArchivePatient({ patient }: { patient: Patient }) {
  const translations = useTranslations();
  const [open, setOpen] = useModal();

  if (patient.archived)
    return (
      <InfoChip
        color="primary"
        label={translations.timeline.patientArchived}
        variant="outlined"
      />
    );

  const canArchive = !patient.auctions?.some((auction) =>
    AUCTION_RUNNING_STATUSES.includes(auction?.status || -1),
  );
  return (
    <>
      <Button
        className="whitespace-nowrap"
        data-testid="archive-patient"
        disabled={!canArchive}
        sx={{
          color: canArchive ? PRIMARY_COLOR : DISABLED_COLOR,
          display: "flex",
          fontSize: FONT_SIZE_13,
          justifyContent: "center",
        }}
        variant="text"
        onClick={() => {
          if (!canArchive) return;
          setOpen(true);
        }}
      >
        {translations.patient.deletePatient.title}
        <ChevronRightIcon
          size={FONT_SIZE_17}
          style={{ fontSize: FONT_SIZE_17 }}
        />
      </Button>

      <ArchivePatientModal
        isOpen={open}
        patient={patient}
        onClose={() => setOpen(false)}
      />
    </>
  );
}
